import {
  FC, useEffect, useLayoutEffect, useMemo, useState, 
} from 'react';
import { useTranslation } from 'react-i18next';

import useCurrency from 'hooks/useCurreny';
import useMaskedInput from 'hooks/useMaskedInput';

import trashIcon from '/trashHelp.svg';

import Select from 'components/Select';
import { CropProps, CropTableProps } from 'utils/types';
import Input from 'components/Input';
import Button from 'components/Button';
import { unMaskInputValue } from 'utils/helpers';
import MaskInput from 'components/MaskedInput';
import { BoardFertilization, FieldProps } from 'features/Croptimizer/types';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';

import { useGenerateCrop } from '../../../hooks/useGenerateCrop';

interface Props {
  setAddCropModal: (value: boolean) => void;
  crop?: CropProps[];
  tableData: CropTableProps[];
  setTableData: (
    tableDataOrUpdater:
    | CropTableProps[]
    | ((prev: CropTableProps[]) => CropTableProps[])
  ) => void;
  fertilizationValues: BoardFertilization | null;
  object: FieldProps['object'];
  cropIndex: number;
}

const AddCropModal: FC<Props> = ({
  setAddCropModal,
  crop = [],
  tableData,
  setTableData,
  fertilizationValues,
  object,
  cropIndex,
}) => {
  const { mtHa, onlyAc, onlyMt } = useCroptimizer();
  const { generateCrop } = useGenerateCrop();
  const { t } = useTranslation();
  const {
    value: price,
    setValue: setPrice,
    clean: cleanprice,
  } = useMaskedInput({ defaultValue: '' });
  const {
    value: yieldAdd,
    setValue: setYieldAdd,
    clean: cleanyieldAdd,
  } = useMaskedInput({ defaultValue: '' });
  const [cropType, setCropType] = useState('');
  const {
    value: n,
    setValue: setN,
    clean: cleanN,
  } = useMaskedInput({ defaultValue: fertilizationValues?.nvalue ?? '' });
  const {
    value: p,
    setValue: setP,
    clean: cleanP,
  } = useMaskedInput({ defaultValue: fertilizationValues?.pvalue ?? '' });
  const {
    value: k,
    setValue: setK,
    clean: cleanK,
  } = useMaskedInput({ defaultValue: fertilizationValues?.kvalue ?? '' });
  const [cropItemData, setCropItemData] = useState<CropTableProps | null>(null);

  const { currencyGreen } = useCurrency();

  const selectOptions = useMemo(() => {
    if (crop) {
      return crop?.map((crop) => ({ value: crop.Crops,
        label: crop.Crops }));
    }

    return [];
  }, [crop]);

  useEffect(() => {
    if (selectOptions) {
      setCropType(selectOptions?.[0]?.value);
    }

    return;
  }, [selectOptions]);

  const initCrop = (currentCropType: string) => {
    const currentCrop = crop.find((item) => item.Crops === currentCropType);

    if (currentCrop) {
      const tableDataItem = generateCrop(
        currentCrop,
        object,
        undefined,
        undefined,
        cropIndex,
      );

      setCropItemData(tableDataItem);
      setYieldAdd(unMaskInputValue(String(tableDataItem.yield)));
      setPrice(unMaskInputValue(String(tableDataItem.price)));
      setN(unMaskInputValue(String(tableDataItem.n)));
      setP(unMaskInputValue(String(tableDataItem.p)));
      setK(unMaskInputValue(String(tableDataItem.k)));
    }
  };

  useLayoutEffect(() => {
    if (crop && !yieldAdd) {
      initCrop(cropType || crop[0].Crops);
    }
  }, [cropType]);

  useEffect(() => {
    if (cropType) {
      const currentCrop = crop.find((item) => item.Crops === cropType);

      if (currentCrop) {
        const data = {
          yield: +cleanyieldAdd,
          price: +cleanprice,
          n: cleanN,
          p: cleanP,
          k: cleanK,
        };

        const tableDataItem = generateCrop(
          currentCrop,
          object,
          undefined,
          data,
          cropIndex,
        );

        setCropItemData(tableDataItem);
      }
    }
  }, [cropType, cleanprice, cleanyieldAdd, cleanN, cleanP, cleanK]);

  const onCropChange = ({ value }: { value: string }) => {
    setCropType(value);

    initCrop(value);
  };

  const onSubmit = () => {
    const getMax = Math.max(...tableData.map((item) => item.id));
    const data = {
      id: getMax + 1,
      name: cropType,
      yield: +cleanyieldAdd,
      price: +cleanprice,
      phIfNoLim: cropItemData?.phIfNoLim,
      limiting: cropItemData?.limiting,
      expectPh: cropItemData?.expectPh,
      yieldPercent: cropItemData?.yieldPercent,
      yieldPrice: cropItemData?.yieldPrice,
      n: cleanN,
      p: cleanP,
      k: cleanK,
      npkGains: cropItemData?.npkGains,
      monps: cropItemData?.monps,
      total: cropItemData?.total,
    } as CropTableProps;
    setTableData((prev = []) => [...(prev as CropTableProps[]), data]);

    setAddCropModal(false);
  };

  return (
    <div className="fixed z-50 top-0 left-0 right-0 bottom-0 bg-[rgba(0,0,0,0.5)] flex items-center justify-center h-full w-screen overflow-y-scroll">
      <div className="bg-white p-8 md:p-4 rounded-2xl md:rounded-none flex flex-col r w-[649px] sm:w-full md:max-w-90 absolute top-[50%] md:top-0 left-[50%] translate-x-[-50%] 2xl:translate-y-[-50%]">
        <div
          className="flex justify-end items-end w-full cursor-pointer"
          onClick={() => setAddCropModal(false)}
        >
          <img src={trashIcon} alt="" />
        </div>
        <div className="font-bold text-2xl md:text-xl text-center">
          {t('addCrop')}
        </div>
        <div className="text-secondaryText mt-2 text-center">
          {t('crop')} #{tableData?.length + 1}
        </div>
        <div className="flex justify-start items-start w-full flex-col">
          <div className="font-bold mt-6 mb-4">{t('cropRotation')}</div>
          <div className="w-full">
            <Select
              title={t('cropRotation')}
              options={selectOptions}
              onChange={onCropChange}
              value={
                cropType?.length > 0
                  ? [{ value: cropType,
                    label: cropType }]
                  : []
              }
            />
          </div>

          <div className="font-bold mt-6 mb-4">{t('crop')}</div>
          <div className="flex justify-between gap-[15.5px] w-full">
            <MaskInput
              className="w-[285px] sm:w-full md:mb-6"
              title={`${t('yield')} (${mtHa})`}
              titleClassName="top-[-9px] left-[8px]"
              value={yieldAdd}
              onChange={setYieldAdd}
            />

            <MaskInput
              className="w-[285px] sm:w-full"
              title={`${t('price')} ${currencyGreen}/${onlyMt}`}
              titleClassName="top-[-9px] left-[8px]"
              value={price}
              onChange={setPrice}
            />
          </div>

          <div className="font-bold mt-4 mb-4">{t('soil')} pH</div>
          <div className="flex justify-between gap-[15.5px] md:flex-wrap w-full">
            <Input
              wrapperCn="sm:shrink sm:grow sm:basis-2/5"
              className="w-[184px] md:w-[285px] sm:!w-full md:mb-6 sm:!mb-0"
              title={t('phNolimeapp')}
              titleClassName="bg-disable-gradient top-[-9px] left-[8px]"
              value={unMaskInputValue(String(cropItemData?.phIfNoLim))}
              disabled
            />
            <Input
              wrapperCn="sm:shrink sm:grow sm:basis-2/5"
              className="w-[184px] md:w-[285px] sm:!w-full md:mb-6 sm:!mb-0"
              title={t('limingStatus')}
              titleClassName="bg-disable-gradient top-[-9px] left-[8px]"
              disabled
              value={cropItemData?.limiting}
            />
            <Input
              wrapperCn="md:w-full"
              className="w-[184px] md:w-full"
              title={t('expect') + 'pH'}
              titleClassName="bg-disable-gradient top-[-9px] left-[8px]"
              disabled
              value={unMaskInputValue(String(cropItemData?.expectPh))}
            />
          </div>

          <div className="font-bold mt-4 mb-4">{t('yieldGains')}</div>
          <div className="flex justify-between gap-[15.5px] md:flex-wrap sm:!flex-nowrap w-full">
            <Input
              wrapperCn="sm:w-1/2"
              className="w-[285px] sm:w-full md:mb-6 sm!:mb-0"
              title="%"
              titleClassName="bg-disable-gradient top-[-9px] left-[8px]"
              disabled
              value={unMaskInputValue(cropItemData?.yieldPercent ?? '')}
            />
            <Input
              wrapperCn="sm:w-1/2"
              className="w-[285px] sm:w-full"
              title={`${currencyGreen}/${onlyAc}`}
              titleClassName="bg-disable-gradient top-[-9px] left-[8px]"
              disabled
              value={unMaskInputValue(String(cropItemData?.yieldPrice))}
            />
          </div>

          <div className="font-bold mt-4 mb-4">
            {t('npkFertilizerdecrease')}
          </div>
          <div className="flex justify-between gap-[15.5px] flex-wrap w-full">
            <MaskInput
              wrapperCn="sm:shrink sm:grow sm:basis-2/5"
              className="w-[184px] md:w-[285px] md:mb-6 sm:!mb-0 sm:!w-full"
              title="N"
              titleClassName="bg-white top-[-9px] left-[8px]"
              value={n}
              onChange={setN}
            />

            <MaskInput
              wrapperCn="sm:shrink sm:grow sm:basis-2/5"
              className="w-[184px] md:w-[285px] md:mb-6 sm:!mb-0 sm:!w-full"
              title={
                <div>
                  P<sub>2</sub>O<sub>5</sub>
                </div>
              }
              titleClassName="bg-white top-[-9px] left-[8px]"
              value={p}
              onChange={setP}
            />

            <MaskInput
              wrapperCn="md:w-full"
              className="w-[184px] md:w-full"
              title={
                <div>
                  K<sub>2</sub>O
                </div>
              }
              titleClassName="bg-white top-[-9px] left-[8px]"
              value={k}
              onChange={setK}
            />
          </div>

          <div className="flex justify-between gap-[15.5px] md:flex-wrap w-full mt-4">
            <div className="flex flex-col sm:w-full">
              <div className="font-bold mb-4">NPK</div>
              <MaskInput
                wrapperCn="sm:w-full"
                className="w-[184px] md:w-[285px] sm:!w-full"
                title={t('gains') + `(${currencyGreen}/${onlyAc})`}
                titleClassName="bg-disable-gradient top-[-9px] left-[8px]"
                disabled
                value={String(Math.round(cropItemData?.npkGains ?? 0))}
              />
            </div>
            <div className="flex flex-col sm:w-full">
              <div className="font-bold mb-4">MO-NPS</div>
              <MaskInput
                className="w-[184px] md:w-[285px] sm:!w-full"
                title={t('gains') + `(${currencyGreen}/${onlyAc})`}
                titleClassName="bg-disable-gradient top-[-9px] left-[8px]"
                disabled
                value={String(cropItemData?.monps)}
              />
            </div>
            <div className="flex flex-col md:w-full">
              <div className="font-bold mb-4">{t('totalGains')}</div>
              <MaskInput
                wrapperCn="md:w-full"
                className="w-[184px] md:w-full"
                title={`${currencyGreen}/${onlyAc}`}
                titleClassName="bg-disable-gradient top-[-9px] left-[8px]"
                disabled
                value={String(cropItemData?.total)}
              />
            </div>
          </div>

          <div className="flex md:flex-col mt-4 md:items-center md:mt-6 gap-[15.5px] md:flex-wrap w-full">
            <Button
              onClick={() => setAddCropModal(false)}
              className="w-[288px] sm:w-1/2"
              btnType="secondary"
            >
              {t('cancel')}
            </Button>
            <Button onClick={onSubmit} className="w-[288px] sm:w-1/2">
              {t('add')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCropModal;
